import { Box, FormControl, IconButton, InputAdornment, TextField } from '@mui/material';
import ClearIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Clear.js';
import SearchIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Search.js';
import { useMemo } from 'react';
import {
  ATLAS_LENS_COMPONENT_VARS,
  onboardingOutlinedInputRootSx,
} from '../Form/formFieldSpec';
import { AtlasLensFormFieldError } from '../Form/AtlasLensFormFieldError';
import { AtlasLensFormFieldWarning } from '../Form/AtlasLensFormFieldWarning';
import { OnboardingFieldLabel } from './OnboardingStepHeader';
import { lensSpacing, useOnboardingLens } from './onboardingLens';

export interface OnboardingFormTextFieldProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  errorId?: string;
  required?: boolean;
  disabled?: boolean;
  /** When true, label is rendered by the parent (e.g. module picker header). */
  hideLabel?: boolean;
  /** Atlas Lens leading icon — Figma input with search adornment. */
  leadingIcon?: 'search';
  inputAriaLabel?: string;
  /** Trailing clear control — Figma search input clear button (node 16118:76810). */
  clearSearchAriaLabel?: string;
  maxLength?: number;
  /** Live hint when input is sanitized (invalid char or length cap). */
  warning?: string;
  warningId?: string;
  onBlur?: () => void;
}

/** Atlas Simple text field — Figma form field with outlined input. */
export function OnboardingFormTextField({
  id,
  label = '',
  value,
  onChange,
  placeholder,
  error,
  errorId,
  required = false,
  disabled = false,
  hideLabel = false,
  leadingIcon,
  inputAriaLabel,
  clearSearchAriaLabel,
  maxLength,
  warning,
  warningId,
  onBlur,
}: OnboardingFormTextFieldProps) {
  const { layout, typography, colors, focusRing, tokens } = useOnboardingLens();
  const { formField } = layout;
  const searchIconSize = lensSpacing(tokens, '3');

  const inputRootSx = useMemo(
    () => ({
      height: formField.inputHeight,
      borderRadius: formField.inputRadius,
      ...onboardingOutlinedInputRootSx,
      '&&.Mui-focused': {
        boxShadow: focusRing,
      },
      ...(error
        ? {
            '& fieldset, & .MuiOutlinedInput-notchedOutline': {
              borderColor: ATLAS_LENS_COMPONENT_VARS.inputOutlinedBorderError,
            },
          }
        : {}),
    }),
    [error, focusRing, formField.inputHeight, formField.inputRadius],
  );

  const describedById = error
    ? (errorId ?? `${id}-error`)
    : warning
      ? (warningId ?? `${id}-warning`)
      : undefined;

  return (
    <FormControl
      error={Boolean(error)}
      fullWidth
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: formField.labelGap,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: formField.labelToInputGap,
          width: '100%',
        }}
      >
        {!hideLabel ? (
          <OnboardingFieldLabel
            htmlFor={id}
            label={label}
            required={required}
            disabled={disabled}
          />
        ) : null}

        <TextField
          id={id}
          fullWidth
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          error={Boolean(error)}
          variant="outlined"
          aria-describedby={describedById}
          slotProps={{
            htmlInput: maxLength != null ? { maxLength } : undefined,
            input: {
              'aria-label': hideLabel ? inputAriaLabel : undefined,
              startAdornment:
                leadingIcon === 'search' ? (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: searchIconSize, color: colors.typeMuted }} />
                  </InputAdornment>
                ) : undefined,
              endAdornment:
                clearSearchAriaLabel && value ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={clearSearchAriaLabel}
                      onClick={() => onChange('')}
                      edge="end"
                      size="small"
                      sx={{ p: lensSpacing(tokens, '0_5') }}
                    >
                      <ClearIcon
                        style={{ fontSize: searchIconSize, color: colors.typeMuted }}
                      />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              ...inputRootSx,
              ...(disabled
                ? {
                    backgroundColor: 'action.disabledBackground',
                    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'action.disabled',
                    },
                  }
                : {}),
            },
            '& .MuiInputAdornment-root': {
              backgroundColor: 'transparent',
              marginRight: 0,
              maxHeight: 'none',
            },
            '& .MuiOutlinedInput-input': {
              px: leadingIcon === 'search' ? 0 : formField.inputPaddingX,
              py: formField.inputPaddingY,
              ml: leadingIcon === 'search' ? formField.labelToInputGap : undefined,
              ...typography.body,
              color: disabled ? 'text.disabled' : colors.typeDefault,
              '&::placeholder': {
                color: disabled ? 'text.disabled' : colors.typeMuted,
                opacity: 1,
              },
            },
          }}
        />
      </Box>
      {error ? <AtlasLensFormFieldError id={describedById} message={error} /> : null}
      {!error && warning ? (
        <AtlasLensFormFieldWarning id={warningId ?? `${id}-warning`} message={warning} />
      ) : null}
    </FormControl>
  );
}
